/*
 * Once Upon Magic — proprietary source code.
 * Copyright © 2026 Once Upon Magic. Unauthorized reproduction or adaptation prohibited.
 * TDM rights reserved. OUM-FP: OUM-20260817-ACTIONS-AC8B2513A3B1
 */
/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

/**
 * OUM cart terms controller.
 *
 * The cart summary is replaced dynamically by Horizon. Inline scripts inside
 * the replaced HTML are not guaranteed to execute, so CGV acceptance and the
 * Pick-Up service fee must be managed by a global script that survives every
 * cart drawer refresh.
 */
const OUM_TERMS_SELECTOR =
  '#PickupTermsCheckbox, #PreorderTermsCheckbox, #GeneralTermsCheckbox';
const OUM_TERMS_SCOPE_SELECTOR =
  '.cart-drawer__summary, .cart-summary__inner, .cart-page__summary';
const OUM_PICKUP_ACCEPTED_KEY = 'oum:pickup-terms-accepted';
const OUM_REOPEN_CART_KEY = 'oum:reopen-cart-after-fee-update';

let oumTermsMutationRunning = false;
let oumTermsObserverScheduled = false;

function oumCartRoot() {
  return window.Shopify?.routes?.root || '/';
}

/**
 * @param {EventTarget | null} node
 * @returns {Element | null}
 */
function oumTermsScope(node) {
  return node instanceof Element ? node.closest(OUM_TERMS_SCOPE_SELECTOR) : null;
}

/**
 * @param {Element} scope
 * @returns {HTMLInputElement[]}
 */
function oumTermsCheckboxes(scope) {
  return [...scope.querySelectorAll(OUM_TERMS_SELECTOR)].filter(
    (checkbox) => checkbox instanceof HTMLInputElement
  );
}

/**
 * @param {Element} scope
 * @param {boolean} accepted
 */
function oumSetTermsButtonState(scope, accepted) {
  const button = scope.querySelector('#RelayMapCta');
  if (!(button instanceof HTMLElement)) return;

  const ready = accepted && scope.getAttribute('data-oum-terms-busy') !== 'true';
  button.setAttribute('aria-disabled', ready ? 'false' : 'true');

  if (ready) {
    button.style.removeProperty('opacity');
    button.style.removeProperty('pointer-events');
    button.style.removeProperty('cursor');
  } else {
    button.style.opacity = '0.45';
    button.style.pointerEvents = 'none';
    button.style.cursor = 'not-allowed';
  }
}

/**
 * @param {Element} scope
 */
function oumSyncTermsScope(scope) {
  const checkboxes = oumTermsCheckboxes(scope);
  if (!checkboxes.length) return;

  const accepted = checkboxes.every((checkbox) => checkbox.checked);
  oumSetTermsButtonState(scope, accepted);

  if (accepted) {
    scope.querySelectorAll('.terms-error').forEach((error) => {
      if (error instanceof HTMLElement) {
        error.hidden = true;
        error.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

function oumSyncAllTermsScopes() {
  document.querySelectorAll(OUM_TERMS_SCOPE_SELECTOR).forEach((scope) => {
    if (!(scope instanceof Element)) return;

    const pickup = scope.querySelector('#PickupTermsCheckbox');
    if (pickup instanceof HTMLInputElement) {
      const feeAlreadyAdded = pickup.dataset.fraisAlready === 'true';
      const acceptedBeforeReload =
        sessionStorage.getItem(OUM_PICKUP_ACCEPTED_KEY) === 'true';

      if (feeAlreadyAdded && acceptedBeforeReload) {
        pickup.checked = true;
      }
    }

    oumSyncTermsScope(scope);
  });
}

/**
 * @param {Element} scope
 * @param {HTMLInputElement} checkbox
 * @param {string} [message]
 */
function oumShowTermsError(scope, checkbox, message) {
  let errorId = 'GeneralTermsError';
  if (checkbox.id === 'PickupTermsCheckbox') errorId = 'PickupTermsError';
  if (checkbox.id === 'PreorderTermsCheckbox') errorId = 'PreorderTermsError';

  const error = scope.querySelector(`#${errorId}`) || scope.querySelector('.terms-error');
  if (error instanceof HTMLElement) {
    if (message) error.textContent = message;
    error.hidden = false;
    error.setAttribute('aria-hidden', 'false');
  }
}

/**
 * @param {string} endpoint
 * @param {Record<string, unknown>} payload
 */
async function oumCartPost(endpoint, payload) {
  const response = await fetch(`${oumCartRoot()}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    credentials: 'same-origin',
    body: JSON.stringify(payload),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.errors) {
    throw new Error(data?.description || data?.errors || 'Mise à jour du panier impossible.');
  }

  return data;
}

/**
 * @param {Element} scope
 * @param {boolean} busy
 */
function oumSetTermsBusy(scope, busy) {
  if (busy) {
    scope.setAttribute('data-oum-terms-busy', 'true');
  } else {
    scope.removeAttribute('data-oum-terms-busy');
  }

  oumTermsCheckboxes(scope).forEach((checkbox) => {
    checkbox.disabled = busy;
  });

  oumSyncTermsScope(scope);
}

function oumReloadKeepingCartOpen() {
  sessionStorage.setItem(OUM_REOPEN_CART_KEY, 'true');
  window.location.reload();
}

function oumTryReopenCartDrawer() {
  if (sessionStorage.getItem(OUM_REOPEN_CART_KEY) !== 'true') return true;

  /** @type {HTMLElement & {open?: () => void} | null} */
  const drawer = document.querySelector('theme-drawer#cart-drawer');
  if (!drawer || typeof drawer.open !== 'function') return false;

  drawer.open();
  sessionStorage.removeItem(OUM_REOPEN_CART_KEY);
  return true;
}

function oumReopenCartDrawerAfterReload() {
  if (sessionStorage.getItem(OUM_REOPEN_CART_KEY) !== 'true') return;
  if (oumTryReopenCartDrawer()) return;

  if ('customElements' in window) {
    customElements.whenDefined('theme-drawer').then(() => {
      if (!oumTryReopenCartDrawer()) {
        window.setTimeout(oumTryReopenCartDrawer, 250);
      }
    });
  }

  window.setTimeout(oumTryReopenCartDrawer, 100);
  window.setTimeout(oumTryReopenCartDrawer, 500);
  window.setTimeout(oumTryReopenCartDrawer, 1000);
}

/**
 * @param {HTMLInputElement} checkbox
 * @param {Element} scope
 */
async function oumHandlePickupTerms(checkbox, scope) {
  if (oumTermsMutationRunning) return;

  const eligible = checkbox.dataset.fraisEligible === 'true';
  const variantId = checkbox.dataset.fraisVariantId || '';
  const lineKey = checkbox.dataset.fraisLineKey || '';
  const alreadyAdded = checkbox.dataset.fraisAlready === 'true';
  const needsUpdate = checkbox.dataset.fraisNeedsUpdate === 'true';

  if (checkbox.checked) {
    sessionStorage.setItem(OUM_PICKUP_ACCEPTED_KEY, 'true');

    if (eligible && variantId && (!alreadyAdded || needsUpdate)) {
      oumTermsMutationRunning = true;
      oumSetTermsBusy(scope, true);

      try {
        if (alreadyAdded && needsUpdate && lineKey) {
          await oumCartPost('cart/change.js', {
            id: lineKey,
            quantity: 0,
          });
        }

        await oumCartPost('cart/add.js', {
          id: variantId,
          quantity: 1,
        });

        // Reload the same storefront page, then reopen the cart drawer so the
        // customer never feels sent back to the previous page.
        oumReloadKeepingCartOpen();
        return;
      } catch (error) {
        console.error('[OUM] Ajout des frais de service Pick-Up :', error);
        sessionStorage.removeItem(OUM_PICKUP_ACCEPTED_KEY);
        sessionStorage.removeItem(OUM_REOPEN_CART_KEY);
        checkbox.checked = false;
        oumShowTermsError(
          scope,
          checkbox,
          'Les frais de service n’ont pas pu être ajoutés. Actualisez le panier puis réessayez.'
        );
      } finally {
        oumTermsMutationRunning = false;
        oumSetTermsBusy(scope, false);
      }

      return;
    }

    oumSyncTermsScope(scope);
    return;
  }

  sessionStorage.removeItem(OUM_PICKUP_ACCEPTED_KEY);

  if (alreadyAdded && lineKey) {
    oumTermsMutationRunning = true;
    oumSetTermsBusy(scope, true);

    try {
      await oumCartPost('cart/change.js', {
        id: lineKey,
        quantity: 0,
      });

      oumReloadKeepingCartOpen();
      return;
    } catch (error) {
      console.error('[OUM] Retrait des frais de service Pick-Up :', error);
      sessionStorage.removeItem(OUM_REOPEN_CART_KEY);
      checkbox.checked = true;
      sessionStorage.setItem(OUM_PICKUP_ACCEPTED_KEY, 'true');
      oumShowTermsError(
        scope,
        checkbox,
        'Les frais de service n’ont pas pu être retirés. Actualisez le panier puis réessayez.'
      );
    } finally {
      oumTermsMutationRunning = false;
      oumSetTermsBusy(scope, false);
    }

    return;
  }

  oumSyncTermsScope(scope);
}

/**
 * Capture the event before the duplicated inline cart scripts can query a
 * checkbox from another cart instance.
 *
 * @param {Event} event
 */
function oumHandleTermsChangeCapture(event) {
  const checkbox = event.target;
  if (
    !(checkbox instanceof HTMLInputElement) ||
    !checkbox.matches(OUM_TERMS_SELECTOR)
  ) {
    return;
  }

  const scope = oumTermsScope(checkbox);
  if (!(scope instanceof Element)) return;

  // The global controller is authoritative for these custom checkboxes.
  event.stopPropagation();

  if (checkbox.id === 'PickupTermsCheckbox') {
    void oumHandlePickupTerms(checkbox, scope);
    return;
  }

  oumSyncTermsScope(scope);
}

/**
 * @param {MouseEvent} event
 */
function oumHandleTermsButtonClick(event) {
  const button =
    event.target instanceof Element ? event.target.closest('#RelayMapCta') : null;
  if (!(button instanceof HTMLElement)) return;

  const scope = oumTermsScope(button);
  if (!(scope instanceof Element)) return;

  const checkboxes = oumTermsCheckboxes(scope);
  const accepted =
    checkboxes.length > 0 && checkboxes.every((checkbox) => checkbox.checked);
  const busy = scope.getAttribute('data-oum-terms-busy') === 'true';

  event.preventDefault();
  event.stopPropagation();

  if (!accepted || busy) {
    const firstUnchecked = checkboxes.find((checkbox) => !checkbox.checked);
    if (firstUnchecked) {
      oumShowTermsError(scope, firstUnchecked);
      firstUnchecked.focus();
      firstUnchecked
        .closest('.terms-wrapper')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    oumSyncTermsScope(scope);
    return;
  }

  const hasGeneralTerms = scope.querySelector('#GeneralTermsCheckbox') !== null;
  const destination = hasGeneralTerms
    ? `${oumCartRoot()}checkout`
    : button.getAttribute('href') ||
      `${oumCartRoot()}pages/choisir-mon-point-relais`;

  window.location.assign(destination);
}

function oumScheduleTermsSync() {
  if (oumTermsObserverScheduled) return;
  oumTermsObserverScheduled = true;

  window.requestAnimationFrame(() => {
    oumTermsObserverScheduled = false;
    oumSyncAllTermsScopes();
  });
}

function oumInitTermsController() {
  oumSyncAllTermsScopes();
  oumReopenCartDrawerAfterReload();

  // Re-check after Horizon and any app scripts have completed their own setup.
  window.setTimeout(oumSyncAllTermsScopes, 100);
  window.setTimeout(oumSyncAllTermsScopes, 500);

  if (document.body) {
    new MutationObserver(oumScheduleTermsSync).observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  document.addEventListener('shopify:section:load', oumScheduleTermsSync);
  window.addEventListener('pageshow', () => {
    oumScheduleTermsSync();
    oumReopenCartDrawerAfterReload();
  });
}

document.addEventListener('change', oumHandleTermsChangeCapture, true);
document.addEventListener('click', oumHandleTermsButtonClick, true);

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', oumInitTermsController, {
    once: true,
  });
} else {
  oumInitTermsController();
}
